import React from 'react';
import PropTypes from 'prop-types';
import { MdTune, MdAdd, MdClose } from 'react-icons/md';

// Record fields → Ransack predicates under `q[<field>_<op>]`.
const RECORD_FIELDS = [
  { value: 'id',                label: 'PIF no.',          numeric: true },
  { value: 'project_q_number',  label: 'Quote number' },
  { value: 'project_j_number',  label: 'Project J-number' },
  { value: 'building_j_number', label: 'P/J number' },
  { value: 'project_name',      label: 'Project name' },
  { value: 'sales_incharge',    label: 'Sales in-charge' },
  { value: 'user_name',         label: 'Created by' },
];

// Area (BDS) fields → matched inside the areas JSONB via `area_conditions`.
// Keys MUST match the backend AREA_FILTER_PATHS allowlist.
const AREA_FIELDS = [
  { value: 'wind_speed',          label: 'Wind speed (BDS1)',      numeric: true },
  { value: 'live_on_frame',       label: 'Live/frame (BDS1)',      numeric: true },
  { value: 'live_on_purlins',     label: 'Live/purlin (BDS1)',     numeric: true },
  { value: 'rainfall',            label: 'Rainfall (BDS1)' },
  { value: 'seismic',             label: 'Seismic (BDS1)' },
  { value: 'frame_type',          label: 'Frame type (BDS1)' },
  { value: 'building_usage',      label: 'Building usage (BDS1)' },
  { value: 'length',              label: 'Length (BDS1)',          numeric: true },
  { value: 'width',               label: 'Width (BDS1)',           numeric: true },
  { value: 'height',              label: 'Height (BDS1)',          numeric: true },
  { value: 'slope',               label: 'Slope (BDS1)' },
  { value: 'bay_spacing',         label: 'Bay spacing (BDS1)' },
  { value: 'roof_profile',        label: 'Roof profile (BDS1)' },
  { value: 'roof_material',       label: 'Roof material (BDS1)' },
  { value: 'roof_color',          label: 'Roof color (BDS1)' },
  { value: 'roof_insulation_type',      label: 'Roof insulation type (BDS1)' },
  { value: 'roof_insulation_thickness', label: 'Roof insulation thickness (BDS1)', numeric: true },
  { value: 'wall_profile',        label: 'Wall profile (BDS1)' },
  { value: 'wall_material',       label: 'Wall material (BDS1)' },
  { value: 'wall_color',          label: 'Wall color (BDS1)' },
  { value: 'wall_insulation_type',      label: 'Wall insulation type (BDS1)' },
  { value: 'wall_insulation_thickness', label: 'Wall insulation thickness (BDS1)', numeric: true },
  { value: 'ridge_vent_type',     label: 'Ridge vent type (BDS2)' },
  { value: 'ridge_vent_length',   label: 'Ridge vent length (BDS2)', numeric: true },
  { value: 'crane_capacity',      label: 'Crane capacity (BDS2)',  numeric: true },
  { value: 'purlin_girt_spacing', label: 'Purlin/girt spacing (BDS3)' },
  { value: 'top_coat_paint',      label: 'Top coat paint (BDS3)' },
];

const ALL_FIELDS  = [...RECORD_FIELDS, ...AREA_FIELDS];
const AREA_KEYS   = new Set(AREA_FIELDS.map((f) => f.value));

const OPS = [
  { value: 'cont',  label: 'contains' },
  { value: 'eq',    label: 'equals' },
  { value: 'start', label: 'starts with' },
  { value: 'gteq',  label: '≥' },
  { value: 'lteq',  label: '≤' },
];

// Area fields only support cont/eq/≥/≤ (no Ransack `start`).
const AREA_OPS = OPS.filter((o) => o.value !== 'start');

const SELECT_CLS = 'tw-px-2 tw-py-1.5 tw-text-sm tw-border tw-border-slate-300 tw-rounded-md tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400';

export const emptyDynamicRow = () => ({ field: 'project_q_number', op: 'cont', value: '' });

const isValid = (r) => r.field && r.op && String(r.value).trim() !== '';

// Unique chip id per row (area conditions are namespaced to avoid colliding with q keys).
export const dynamicRowTagId = (r) =>
  (AREA_KEYS.has(r.field) ? `area:${r.field}_${r.op}` : `${r.field}_${r.op}`);

// Splits rows into the two request channels the backend understands.
export const buildDynamicPayload = (rows = []) => {
  const q = {};
  const area_conditions = [];
  rows.filter(isValid).forEach((r) => {
    const value = String(r.value).trim();
    if (AREA_KEYS.has(r.field)) area_conditions.push({ field: r.field, op: r.op, value });
    else q[`${r.field}_${r.op}`] = value;
  });
  return { q, area_conditions };
};

// Human-readable chips for active conditions.
export const buildDynamicTags = (rows = []) =>
  rows.filter(isValid).map((r) => {
    const field = ALL_FIELDS.find((f) => f.value === r.field);
    const op = OPS.find((o) => o.value === r.op);
    return { id: dynamicRowTagId(r), name: `${field?.label || r.field} ${op?.label || r.op}`, value: String(r.value).trim() };
  });

// Controlled, presentational precise-condition builder. Rendered inside the
// More Filters drawer; the drawer owns Apply/Reset.
const DynamicFilters = ({ value = [], onChange }) => {
  const rows = value.length ? value : [emptyDynamicRow()];

  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add    = () => onChange([...rows, emptyDynamicRow()]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  const changeField = (i, field) => {
    // Reset op if the current one isn't valid for the new field's type.
    const isArea = AREA_KEYS.has(field);
    const ops = isArea ? AREA_OPS : OPS;
    const current = rows[i].op;
    update(i, { field, op: ops.some((o) => o.value === current) ? current : 'cont' });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-slate-600 tw-font-medium tw-text-sm">
        <MdTune size={15} className="tw-text-emerald-600" />
        Precise conditions
        <span className="tw-font-normal tw-text-slate-400 tw-text-xs">— exact match on record or Area (BDS) values</span>
      </div>

      {rows.map((r, i) => {
        const isArea = AREA_KEYS.has(r.field);
        const field = ALL_FIELDS.find((f) => f.value === r.field);
        const ops = isArea ? AREA_OPS : OPS;
        return (
          <div key={i} className="tw-flex tw-items-center tw-gap-1.5">
            <select className={`${SELECT_CLS} tw-w-40`} value={r.field} onChange={(e) => changeField(i, e.target.value)}>
              <optgroup label="Record">
                {RECORD_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </optgroup>
              <optgroup label="Area (BDS)">
                {AREA_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </optgroup>
            </select>
            <select className={`${SELECT_CLS} tw-w-24`} value={r.op} onChange={(e) => update(i, { op: e.target.value })}>
              {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              type={field?.numeric ? 'number' : 'text'}
              value={r.value}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="Value"
              className={`${SELECT_CLS} tw-flex-1 tw-min-w-0`}
            />
            <button type="button" onClick={() => remove(i)} className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-50 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex-shrink-0">
              <MdClose size={16} />
            </button>
          </div>
        );
      })}

      <button type="button" onClick={add} className="tw-inline-flex tw-items-center tw-gap-1 tw-self-start tw-px-2 tw-py-1 tw-rounded-sm tw-text-emerald-600 hover:tw-bg-emerald-50 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-font-medium">
        <MdAdd size={16} /> Add condition
      </button>
    </div>
  );
};

DynamicFilters.propTypes = {
  value:    PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

export default DynamicFilters;
